import _ from 'underscore';

import { Store } from 'redux';
import { QueryString } from '../../../common/react/QueryString';
import { Order } from '../../../ts/helpers/Order';
import { ContentFilterKeyName, ContentFiltersKeys, ContentTypeFilterKey } from '../../../ts/models/ContentFilters';
import { ExplorableItemType } from '../../../ts/models/ContentType';
import { getDefaultOrderForContentTypesOrSpaceInfo } from '../../../ts/models/sort/SortOptionDefinitions';
import { filterToContentTypes } from '../../../ts/ServerApi/types/IContent';
import { IOrderClause } from '../../../ts/ServerApi/types/IOrderClause';
import { setOrderAction } from '../../actions/SetOrderAction';
import { contentTypeFilterSelected } from '../../actions/spaces/ContentExplorerFilterActions';
import { mixedContentFilterApplied } from '../../actions/spaces/filters/FilterActions';
import { sortContent } from '../../actions/spaces/SortContentAction';
import {
  selectCurrentlyApplicableContentTypes,
  selectDefaultContentTypeFilterValues,
} from '../../selectors/ContentFilterSelectors';
import { selectContentSort } from '../../selectors/ContentSortSelectors';
import { selectSpaceInfo } from '../../selectors/SpaceInfoSelectors';
import { AppState } from '../../state/AppState';
import { UrlParamKeys } from '../../UrlParamKeys';

/*
  There is a "3-way binding" between the Explorer, the URL query params, and the Redux store.
  This is achieved with the help of these Angular watchers that must be instantiated on any page that
  includes an Explorer.

  The URL is the ultimate source of truth in the 3-way binding. When any action in the Explorer
  necessitates a change in the query param, the React component will manually update the URL. At that
  point, these watchers will detect the update and dispatch a Redux action to keep the store in sync,
  which will then update the Explorer again via its store subscription. This design allows the Redux
  store (and, consequently, the Explorer) to stay in sync in the same way if the user modifies the URL
  manually.

  The URL updates first which triggers the watchers. However, we don't want the actions fired off
  if we're actually changing locations (paths).

  IMPORTANT: The redux actions dispatched by these watchers should be called only from the watchers.
  If they are called from anywhere else, the URL will not be updated when these actions are taken.

 */

export interface AddUrlWatchersToScopeForExplorerArgs {
  $scope: ng.IScope;
  $location: ng.ILocationService;
  queryString: QueryString;
  store: Store<AppState>;
  getPreviousPath: () => string;
}

export interface WatchUrlParamsArgs {
  readonly contentType: boolean;
  readonly filters: boolean;
  readonly sort: boolean;
}

export function addUrlWatchersToScopeForExplorer(args: AddUrlWatchersToScopeForExplorerArgs, doWatchParams: WatchUrlParamsArgs = {contentType: true, filters: true, sort: true}) {
  if (doWatchParams.contentType) {
    watchContentTypeParam(args);
  }

  if (doWatchParams.sort) {
    watchOrderParam(args);
  }

  if (doWatchParams.filters) {
    watchFilterParams(args);
  }
}

function watchContentTypeParam(params: AddUrlWatchersToScopeForExplorerArgs) {
  const { $scope, $location, queryString, store, getPreviousPath } = params;

  $scope.$watch(() => queryString()[ContentTypeFilterKey], (newValue: string, prevValue: string) => {
    if (newValue !== prevValue && getPreviousPath() === $location.path()) {
      const defaultValues = selectDefaultContentTypeFilterValues(store.getState());
      // We tell the action that we aren't clearAndSet content filter types to enable for optimizations
      store.dispatch(contentTypeFilterSelected(getContentTypesFromQueryParam(newValue, defaultValues), false /*clearAndSet*/));
    }
  });
}

function watchOrderParam(params: AddUrlWatchersToScopeForExplorerArgs) {
  const { $scope, $location, queryString, store, getPreviousPath } = params;

  $scope.$watch(() => queryString()[UrlParamKeys.Order], (newValue: string, prevValue: string) => {
    if (newValue !== prevValue && getPreviousPath() === $location.path()) {
      const contentTypes = selectCurrentlyApplicableContentTypes(store.getState());
      const spaceInfo = selectSpaceInfo(store.getState());
      const defaultOrder = getDefaultOrderForContentTypesOrSpaceInfo(contentTypes, spaceInfo);
      const orderFromQuery = getOrderClausesFromQueryParam(newValue, defaultOrder);

      if (selectContentSort(store.getState()).length === 0) {
        // This should only occur during the initial page load - default app state.
        // At this point, set the order in app state, but don't re-fetch the content.
        store.dispatch(setOrderAction(orderFromQuery));
      } else {
        store.dispatch(sortContent(orderFromQuery));
      }
    }
  });
}

function watchFilterParams(params: AddUrlWatchersToScopeForExplorerArgs) {
  const { $scope, $location, queryString, store, getPreviousPath } = params;

  for (const filterType of _.keys(ContentFiltersKeys)) {
    $scope.$watch(() => queryString()[filterType], (newValue: string, prevValue: string) => {
      if (newValue !== prevValue && getPreviousPath() === $location.path()) {
        if (newValue && !_.isString(newValue)) {
          newValue = String(newValue);
        }
        store.dispatch(mixedContentFilterApplied(filterType as ContentFilterKeyName, newValue));
      }
    });
  }
}

export function getContentTypesFromQueryParam(
  queryParam: string,
  defaultValues: ReadonlyArray<ExplorableItemType>,
): ReadonlyArray<ExplorableItemType> {
  if (queryParam === '') {
    return [];
  } else if (queryParam === null || queryParam === undefined) {
    return defaultValues;
  }
  return filterToContentTypes(queryParam.split(','));
}

export function getOrderClausesFromQueryParam(queryParam: string, defaultOrder: IOrderClause[]): IOrderClause[] {
  if (queryParam === '') {
    return [];
  } else if (queryParam === null || queryParam === undefined) {
    return defaultOrder;
  }
  return Order.fromInput(queryParam);
}
